import { useMemo, useState } from 'react';

type Props = {
  fecha: string;
  nombreFeriado: string;
  bloquesIniciales: Array<{ inicio: string; fin: string; cupos: number }>;
  habilitadoInicial: boolean;
  onSave: (payload: {
    habilitado: boolean;
    bloques: Array<{ inicio: string; fin: string; cupos: number }>;
  }) => Promise<void> | void;
  onClose: () => void;
};

export default function ConfigurarFeriadoHorarioModal({
  fecha,
  nombreFeriado,
  bloquesIniciales,
  habilitadoInicial,
  onSave,
  onClose,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [habilitado, setHabilitado] = useState(habilitadoInicial);
  const [bloques, setBloques] = useState<Array<{ id: string; inicio: string; fin: string; cupos: number }>>(
    bloquesIniciales.map((b, i) => ({
      id: `${i}-${b.inicio}-${b.fin}`,
      inicio: b.inicio,
      fin: b.fin,
      cupos: b.cupos,
    }))
  );
  const [error, setError] = useState('');

  const fechaTexto = useMemo(
    () =>
      new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }),
    [fecha]
  );

  const toMinutes = (hhmm: string): number => {
    const [h, m] = (hhmm || '').split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return -1;
    return h * 60 + m;
  };

  const addBloque = () => {
    setBloques((prev) => [
      ...prev,
      { id: crypto.randomUUID(), inicio: '09:00', fin: '10:00', cupos: 10 },
    ]);
  };

  const removeBloque = (id: string) => {
    setBloques((prev) => prev.filter((b) => b.id !== id));
  };

  const updateBloque = (id: string, patch: Partial<{ inicio: string; fin: string; cupos: number }>) => {
    setBloques((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const validateBloques = (): string => {
    const clean = bloques.map((b) => ({ ...b, cupos: Math.max(0, Math.min(999, Number(b.cupos || 0))) }));
    for (const b of clean) {
      const ini = toMinutes(b.inicio);
      const fin = toMinutes(b.fin);
      if (ini < 0 || fin < 0) return 'Todos los bloques deben tener hora válida.';
      if (ini >= fin) return `Bloque inválido ${b.inicio} - ${b.fin}: inicio debe ser menor al término.`;
      if (b.cupos <= 0) return `Bloque ${b.inicio} - ${b.fin}: cupos debe ser mayor a 0.`;
    }
    const sorted = [...clean].sort((a, b) => toMinutes(a.inicio) - toMinutes(b.inicio));
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      if (toMinutes(curr.inicio) < toMinutes(prev.fin)) {
        return `Bloques solapados: ${prev.inicio}-${prev.fin} y ${curr.inicio}-${curr.fin}.`;
      }
    }
    return '';
  };

  const handleSave = async () => {
    setError('');
    if (habilitado) {
      if (bloques.length === 0) {
        setError('Debes agregar al menos un bloque para habilitar este feriado.');
        return;
      }
      const err = validateBloques();
      if (err) {
        setError(err);
        return;
      }
    }

    try {
      setSaving(true);
      await onSave({
        habilitado,
        bloques: bloques
          .map((b) => ({ inicio: b.inicio, fin: b.fin, cupos: Math.max(0, Math.min(999, Number(b.cupos || 0))) }))
          .sort((a, b) => toMinutes(a.inicio) - toMinutes(b.inicio)),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl">
        <div className="border-b border-gray-200 p-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Horario especial para feriado</h3>
              <p className="mt-1 text-sm text-gray-600">
                {nombreFeriado} · {fechaTexto}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          <label className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={habilitado}
              onChange={(e) => setHabilitado(e.target.checked)}
              className="h-4 w-4"
            />
            Habilitar atención en este feriado
          </label>

          <div className="mb-3 text-sm text-gray-600">
            Si está habilitado, define bloques exclusivos para este feriado (inicio, término y cupos).
          </div>
          {habilitado && (
            <>
              <div className="mb-3">
                <button
                  onClick={addBloque}
                  className="rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
                >
                  <i className="ri-add-line mr-1" />
                  Agregar bloque
                </button>
              </div>

              <div className="space-y-2">
                {bloques.map((bloque, idx) => (
                  <div
                    key={bloque.id}
                    className="grid grid-cols-[60px_1fr_1fr_110px_44px] items-center gap-2 rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <div className="text-xs font-semibold text-gray-500">#{idx + 1}</div>
                    <input
                      type="time"
                      value={bloque.inicio}
                      onChange={(e) => updateBloque(bloque.id, { inicio: e.target.value })}
                      className="rounded-md border border-gray-300 px-2 py-2 text-sm"
                    />
                    <input
                      type="time"
                      value={bloque.fin}
                      onChange={(e) => updateBloque(bloque.id, { fin: e.target.value })}
                      className="rounded-md border border-gray-300 px-2 py-2 text-sm"
                    />
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={bloque.cupos}
                      onChange={(e) => updateBloque(bloque.id, { cupos: Number(e.target.value || 0) })}
                      className="rounded-md border border-gray-300 px-2 py-2 text-sm"
                    />
                    <button
                      onClick={() => removeBloque(bloque.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Eliminar bloque"
                    >
                      <i className="ri-delete-bin-line text-lg" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {error && <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Guardar horario especial'}
          </button>
        </div>
      </div>
    </div>
  );
}
